import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import bgImage from "../../assets/images/37.png";
import { Icons } from "../../constants/icon-library";
import { loginUser, setUser } from "../../features/user/userSlice";
import { ROLE } from "../../constants/constants";
import {
  openAuthLink,
  serverUrl,
} from "../../platforms/AssistantDetails/Assistants.utils";
import { getRedirectPath, saveAuthToken } from "../../utils/authHeader";

function SignIn() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading } = useSelector((state) => state.user);

  const goToForgotPassword = () => {
    navigate("/forgot-password");
  };

  useEffect(() => {
    if (user?.role === ROLE.USER) {
      if (user?.isOnboardingCompleted) {
        navigate("/meetings", { replace: true });
      } else {
        navigate("/onboarding/source", { replace: true });
      }
    } else if (user?.role === ROLE.ADMIN) {
      navigate("/admin/users", { replace: true });
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await dispatch(
        loginUser({ email: formData.email, password: formData.password }),
      ).unwrap();

      const { token, ...userData } = response;
      const role = response.role || "";

      if (token) {
        saveAuthToken(token);
      }

      dispatch(setUser({ ...userData, role: response.role }));

      toast.success("Login successful");

      const path = getRedirectPath({
        role,
        isOnboardingCompleted: response.isOnboardingCompleted,
      });

      navigate(path, { replace: true });
    } catch (err) {
      let message =
        err ||
        err?.response?.message ||
        err?.response?.data?.message ||
        "Login failed.";

      if (message.includes("Account not verified.")) {
        toast.warn(message);
        navigate("/verify", { state: { email: formData.email } });
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <main className="main" id="top">
      <div className="container-fluid bg-body-tertiary dark__bg-gray-1200">
        <div
          className="bg-holder bg-auth-card-overlay"
          style={{ backgroundImage: `url(${bgImage})` }}
        ></div>

        <div className="row flex-center position-relative min-vh-100 g-0 py-5">
          <div className="col-11 col-sm-10 col-xl-8">
            <div className="card border border-translucent auth-card">
              <div className="card-body pe-md-0">
                <div className="row align-items-center gx-0 gy-7">
                  <div className="col mx-auto py-8">
                    <div className="auth-form-box">
                      <div className="text-center mb-7">
                        <Link
                          className="d-flex flex-center text-decoration-none mb-4"
                          to="/"
                        >
                          <div className="d-flex align-items-center fw-bolder fs-3 d-inline-block">
                            <img
                              src={
                                localStorage.getItem("phoenixTheme") === "dark"
                                  ? "/assets/img/icons/logo.png"
                                  : "/assets/img/icons/logo-dark.png"
                              }
                              alt="phoenix"
                              width="150"
                            />
                          </div>
                        </Link>
                        <h3 className="text-body-highlight">Sign In</h3>
                        <p className="text-body-tertiary">
                          Get access to your account
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-phoenix-secondary w-100 mb-2"
                        onClick={() => openAuthLink(`${serverUrl}/auth/google`)}
                      >
                        <img
                          src="/assets/img/icons/google.png"
                          alt="google"
                          className="mx-1"
                          style={{ width: "16px", height: "16px" }}
                        />
                        Login with Google
                      </button>
                      <button
                        type="button"
                        className="btn btn-phoenix-secondary w-100 mb-2"
                        onClick={() => openAuthLink(`${serverUrl}/auth/github`)}
                      >
                        <img
                          src="/assets/img/icons/github.png"
                          alt="github"
                          className="mx-1 "
                          style={{ width: "16px", height: "16px" }}
                        />
                        Login with Github
                      </button>
                      <div className="d-flex align-items-center mt-5 mb-4">
                        <div className="flex-grow-1 border-top"></div>
                        <span className="mx-3 small fw-bold text-body-tertiary">
                          or use email
                        </span>
                        <div className="flex-grow-1 border-top"></div>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="mb-3 text-end">
                          <label className="form-label" htmlFor="email">
                            Email address
                          </label>
                          <div className="form-icon-container">
                            <input
                              className="form-control form-icon-input"
                              id="email"
                              type="email"
                              placeholder="name@example.com"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                            />
                            <i
                              className={`wb ${Icons(
                                "user",
                              )} fs-9 text-body form-icon`}
                            ></i>
                          </div>
                        </div>

                        <div className="mb-3 text-end">
                          <label className="form-label" htmlFor="password">
                            Password
                          </label>
                          <div
                            className="form-icon-container"
                            data-password="data-password"
                          >
                            <input
                              className="form-control form-icon-input"
                              id="password"
                              type="password"
                              placeholder="Password"
                              value={formData.password}
                              onChange={handleInputChange}
                              required
                            />
                            <i
                              className={`wb ${Icons(
                                "key",
                              )} fs-9 text-body form-icon`}
                            ></i>
                          </div>
                        </div>

                        <div className="row flex-between-center mb-5">
                          <div className="col-auto"></div>
                          <div className="col-auto">
                            <button
                              className="fs-9 fw-semibold btn btn-link p-0"
                              onClick={goToForgotPassword}
                              type="button"
                            >
                              Forgot Password?
                            </button>
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="btn btn-primary w-100 mb-3"
                          disabled={loading}
                        >
                          {loading ? (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : null}
                          {loading ? "Signing In..." : "Sign In"}
                        </button>
                      </form>

                      <div className="text-center">
                        <Link className="fs-9 fw-bold" to="/sign-up">
                          Create an account
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default SignIn;
